import { FieldArray } from "formik";
import { Box, Button, Select, Group, Switch } from "@mantine/core";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { TextEditorInput } from "../UI/FormField";
import { SingleImageUploader } from "../UI/ImageHandler";
import { imageBaseUrl } from "../../utilities/constants";
import HeadingParagraphBlock from "../UI/HeadingParagraphBlock";
import LinkBlockEditor from "../UI/LinkBlock";

const blockTypes = [
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
  { value: "h4", label: "Heading 4" },
  { value: "h5", label: "Heading 5" },
  { value: "h6", label: "Heading 6" },
  { value: "p", label: "Paragraph" },
  { value: "image", label: "Image" },
  { value: "link", label: "Link" },
  { value: "rich", label: "Rich Text" },
  { value: "hr", label: "Horizontal Rule" },
];

function isValidImageUrl(url) {
  return /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url);
}

export default function EmailFooterCreate({ values, setFieldValue }) {
  const [selectedBlock, setSelectedBlock] = useState("");
  const [imageMode, setImageMode] = useState({});

  return (
    <FieldArray name='footerBlocks'>
      {({ push, remove }) => (
        <>
          {values?.footerBlocks?.map((block, index) => {
            const isUploadMode = imageMode[index] ?? false;

            return (
              <div
                key={block?.id}
                className='dark:bg-black bg-white p-3 border border-dotted rounded'
              >
                <div className='flex justify-between'>
                  <strong className='text-lg'>
                    {block?.type?.toUpperCase()}
                  </strong>
                  <Button
                    size='xs'
                    variant='outline'
                    color='red'
                    onClick={() => remove(index)}
                  >
                    Remove
                  </Button>
                </div>

                {block.type === "rich" && (
                  <div className='relative overflow-visible z-50'>
                    <TextEditorInput
                      name={`footerBlocks[${index}].value`}
                      label='Rich Text'
                      defaultValue={block.value}
                    />
                  </div>
                )}

                {["h1", "h2", "h3", "h4", "h5", "h6", "p"].includes(
                  block.type
                ) && (
                  <HeadingParagraphBlock
                    block={block}
                    index={index}
                    setFieldValue={setFieldValue}
                  />
                )}

                {block.type === "link" && (
                  <LinkBlockEditor
                    block={block}
                    index={index}
                    setFieldValue={setFieldValue}
                  />
                )}

                {block.type === "hr" && (
                  <div className='mt-4'>
                    <hr
                      style={{
                        margin: block.styles?.margin || "1px 0",
                        border: block.styles?.border || "1px solid #ccc",
                        width: block.styles?.width || "100%",
                      }}
                    />
                    <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mt-4'>
                      <div className='flex flex-col space-y-1'>
                        <label className='text-sm font-medium'>Margin</label>
                        <input
                          type='text'
                          placeholder='e.g. 10px 0'
                          defaultValue={block.styles?.margin || ""}
                          onBlur={(e) =>
                            setFieldValue(
                              `footerBlocks[${index}].styles.margin`,
                              e.target.value
                            )
                          }
                          className='border bg-white dark:bg-gray-800 rounded px-2 py-1'
                        />
                      </div>
                      <div className='flex flex-col space-y-1'>
                        <label className='text-sm font-medium'>Border</label>
                        <input
                          type='text'
                          placeholder='e.g. 1px solid #ccc'
                          defaultValue={block.styles?.border || ""}
                          onBlur={(e) =>
                            setFieldValue(
                              `footerBlocks[${index}].styles.border`,
                              e.target.value
                            )
                          }
                          className='border bg-white dark:bg-gray-800 rounded px-2 py-1'
                        />
                      </div>
                    </div>
                  </div>
                )}

                {block.type === "image" && (
                  <>
                    <Group position='left' mt='sm'>
                      <Switch
                        checked={isUploadMode}
                        onChange={(e) => {
                          setImageMode((prev) => ({
                            ...prev,
                            [index]: e.target.checked,
                          }));
                        }}
                        label={isUploadMode ? "Using Upload" : "Using URL"}
                      />
                    </Group>

                    {isUploadMode || block.isUploadMode ? (
                      <SingleImageUploader
                        title='Upload Image'
                        values={{ value: block.value }}
                        field='value'
                        setFieldValue={(fieldName, value) => {
                          setFieldValue(
                            `footerBlocks[${index}].${fieldName}`,
                            value
                          );
                          setFieldValue(
                            `footerBlocks[${index}].isUploadMode`,
                            true
                          );
                        }}
                      />
                    ) : (
                      <>
                        <input
                          type='url'
                          placeholder='Image URL'
                          value={block.value || ""}
                          onChange={(e) => {
                            setFieldValue(
                              `footerBlocks[${index}].value`,
                              e.target.value
                            );
                            setFieldValue(
                              `footerBlocks[${index}].isUploadMode`,
                              false
                            );
                          }}
                          className='w-full mt-2 border px-2 py-1 rounded'
                        />

                        {/* Validation for image URL */}
                        {block.value && !isValidImageUrl(block.value) && (
                          <p className='text-red-600 text-sm mt-1'>
                            ⚠️ Please enter a valid image URL (jpg, jpeg, png,
                            gif, webp)
                          </p>
                        )}

                        {block.value && isValidImageUrl(block.value) && (
                          <img
                            src={
                              block.value.startsWith("http")
                                ? block.value
                                : `${imageBaseUrl}/${block.value}`
                            }
                            alt='preview'
                            className='mt-4 rounded max-h-60 object-contain border'
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}

          <div className='dark:bg-black text-black dark:text-white bg-white p-3 border border-dotted rounded'>
            <Group grow>
              <Select
                placeholder='Select block type'
                data={blockTypes}
                value={selectedBlock}
                onChange={setSelectedBlock}
                styles={{
                  input: {
                    border: "#969696",
                    color: "#969696",
                    backgroundColor: "#0000",
                  },
                }}
              />
              <Button
                variant='light'
                onClick={() => {
                  if (!selectedBlock) return;
                  push({
                    id: uuidv4(),
                    type: selectedBlock,
                    value: "",
                  });
                  setSelectedBlock("");
                }}
              >
                ➕ Add Block
              </Button>
            </Group>
          </div>
        </>
      )}
    </FieldArray>
  );
}
